import { useState } from 'react';
import { useIntl } from 'react-intl';
import { showNotification } from 'sonata-react-components';

import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { useGrouping } from '../../hooks/useGrouping';
import { DeleteGroupPrompt } from '../DeleteGroupPrompt/DeleteGroupPrompt';
import { formatGroupingNotice } from './groupingNotification';

import type { ReactNode } from 'react';
import type { GroupFamily, GroupReferences } from '../../domain/grouping.types';

interface PendingDeletion {
  groupName: string;
  itemIds: string[];
  references: GroupReferences;
}

export interface UseItemsDeletionResult {
  /** Deletes list items; groups they leave empty are deleted too (asking first if ads / temporal pacing use them). */
  deleteItems: (itemIds: string[]) => void;
  /** Confirmation to render while a deletion waits for the user. */
  prompt: ReactNode;
}

/** Item deletion of a list family that keeps its groups consistent (no empty groups left behind). */
export function useItemsDeletion(family: GroupFamily): UseItemsDeletionResult {
  const { formatMessage } = useIntl();
  const { deleteMultipleItems } = useMultiFlightMap();
  const grouping = useGrouping(family);
  const [pending, setPending] = useState<PendingDeletion | null>(null);

  const remove = (itemIds: string[]) => {
    setPending(null);
    // Groups first: the item deletion below must see (and keep) the cleaned memberships
    const { deletedGroups } = grouping.applyGroupChange({ itemIds, type: 'deleteItems' });
    deleteMultipleItems(family, itemIds);
    deletedGroups.forEach(({ name }) => {
      showNotification('success', formatGroupingNotice(formatMessage, family, { groupName: name, kind: 'deleted' }));
    });
  };

  const deleteItems = (itemIds: string[]) => {
    const { deletedGroups, references } = grouping.previewGroupChange({ itemIds, type: 'deleteItems' });
    if (references.ads + references.temporalPacing > 0) {
      setPending({ groupName: deletedGroups.map(({ name }) => name).join(', '), itemIds, references });
      return;
    }
    remove(itemIds);
  };

  const prompt = pending && (
    <DeleteGroupPrompt
      groupName={pending.groupName}
      onCancel={() => setPending(null)}
      onConfirm={() => remove(pending.itemIds)}
      references={pending.references}
    />
  );

  return { deleteItems, prompt };
}
